import type { FC } from 'react'
import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  // Bell01,
  Shield01,
  Building01,
  LayersThree01,
  Users01,
  // List,
  ShieldTick,
  // Mail01,
  LifeBuoy01,
  Share03,
  User01,
  CoinsStacked01,
  Receipt,
  // Zap,
  // ArrowLeft,
} from '@untitled-ui/icons-react'
import { intelligentPreparationViolet } from '@/components/AgenticPreparationIndicator'
import { NabuAssistantGlyph } from '@/components/NabuAssistantIcon'
import { useAuth } from '@/hooks/use-auth'
import useTranslate from '@/hooks/use-translate'
import { callAccountGroups } from '@/api'
import { countAgencyGroups } from '@/sections/settings/AgenciesSettings/agencies-utils'
import { useSettingsAccess } from '@/sections/settings/org-settings-access'
import { useBillingSubscription } from '@/sections/settings/SubscriptionSettings/hooks/use-billing'
import { useHyperlineFeature } from '@/sections/settings/SubscriptionSettings/hooks/use-hyperline-feature'
import { paths } from '@/paths'
import { tokens } from '@/locales/tokens'
import { densityPx } from '@/theme/base/density'
import { useFlattenedOrganizationMembers } from '@/sections/settings/MembersSettings/use-flattened-organization-members'
import { FeatureNames } from '@/lib/featureFlags'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { useAuthzScreens } from '@/hooks/use-authz'
import { useConfirmLeave } from './settings-unsaved-changes'
import { APP_FRAME_GAP } from '@/constants/appFrame'

const NAV_ICON_PX = densityPx(13)
const DOC_ICON_PX = densityPx(10)

type SettingsTokenKey = keyof typeof tokens.settings

interface NavItem {
  id: string
  labelKey: SettingsTokenKey
  icon: React.ElementType
  path: string
  featureFlag?: FeatureNames
  /** Facturation : visible uniquement si org enrolled (Billing V2). */
  requiresEnrollment?: boolean
  /** Abonnement / Facturation : ADMIN + SUPERADMIN uniquement. */
  billingAdminOnly?: boolean
  /** Rôles : flag et organisation en mode actif (`useAuthzScreens`). */
  requiresAuthzScreens?: boolean
  /** Couleur propre à l'entrée (icône, état actif) à la place du primary. */
  accentColor?: string
}

interface NavGroup {
  labelKey: SettingsTokenKey
  adminOnly?: boolean
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'groupYourAccount',
    items: [
      {
        id: 'profile',
        labelKey: 'navProfile',
        icon: User01,
        path: paths.dashboard.settings.account.profile,
      },
      // Notifications are for a V2 of the org settings, so we're hiding them for now
      // {
      //   id: 'notifications',
      //   labelKey: 'navNotifications',
      //   icon: Bell01,
      //   path: paths.dashboard.settings.account.notifications,
      // },
      {
        id: 'security',
        labelKey: 'navSecurity',
        icon: Shield01,
        path: paths.dashboard.settings.account.security,
      },
    ],
  },
  {
    labelKey: 'groupOrganization',
    adminOnly: true,
    items: [
      {
        id: 'org',
        labelKey: 'navOrganization',
        icon: Building01,
        path: paths.dashboard.settings.org.general,
      },
      // Form customization is hidden until the feature is ready to ship.
      // {
      //   id: 'formulaire',
      //   labelKey: 'navFormCustomization',
      //   icon: List,
      //   path: paths.dashboard.settings.org.formulaire,
      //   featureFlag: FeatureNames.formCustomization,
      // },
      {
        id: 'agencies',
        labelKey: 'navAgencies',
        icon: LayersThree01,
        path: paths.dashboard.settings.org.agencies,
      },
      {
        id: 'members',
        labelKey: 'navMembers',
        icon: Users01,
        path: paths.dashboard.settings.org.members,
      },
      {
        id: 'roles',
        labelKey: 'navRoles',
        icon: ShieldTick,
        path: paths.dashboard.settings.org.roles,
        requiresAuthzScreens: true,
      },
      // Email Templates are for a V2 of the org settings, so we're hiding them for now
      // {
      //   id: 'email-templates',
      //   labelKey: 'navEmailTemplates',
      //   icon: Mail01,
      //   path: paths.dashboard.settings.org.emailTemplates,
      // },
      {
        id: 'subscription',
        labelKey: 'navSubscription',
        icon: CoinsStacked01,
        path: paths.dashboard.settings.org.subscription,
        billingAdminOnly: true,
        featureFlag: FeatureNames.hyperline,
      },
      {
        id: 'billing',
        labelKey: 'navBilling',
        icon: Receipt,
        path: paths.dashboard.settings.org.billing,
        requiresEnrollment: true,
        billingAdminOnly: true,
        featureFlag: FeatureNames.hyperline,
      },
    ],
  },
  {
    labelKey: 'groupModules',
    items: [
      {
        id: 'ai-agents',
        labelKey: 'navAiAgents',
        icon: NabuAssistantGlyph,
        accentColor: intelligentPreparationViolet,
        path: paths.dashboard.settings.modules.nabuAssistant,
        featureFlag: FeatureNames.smartPreparation,
      },
    ],
  },
  {
    labelKey: 'groupSupport',
    items: [
      {
        id: 'help',
        labelKey: 'navHelpCenter',
        icon: LifeBuoy01,
        path: paths.dashboard.settings.support.help,
      },
    ],
  },
]

function MembersNavCountChip() {
  const theme = useTheme()
  const { members, isLoading } = useFlattenedOrganizationMembers()
  if (isLoading) return null
  return (
    <Chip
      label={members.length}
      size="small"
      sx={{
        height: 20,
        minWidth: 22,
        flexShrink: 0,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.neutral?.[700] ?? '#374151', 0.6)
            : theme.palette.grey[200],
        color: theme.palette.neutral?.[600] ?? theme.palette.text.secondary,
        fontWeight: 600,
        '& .MuiChip-label': {
          px: 0.75,
          fontSize: 10,
          lineHeight: 1.2,
        },
      }}
    />
  )
}

export const SettingsSidebar: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const confirmLeave = useConfirmLeave()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { isFeatureEnabled } = useFeatureFlags()
  const authzScreens = useAuthzScreens()
  const { t } = useTranslate('settings')

  // Les rôles d'avant, ou en mode actif les droits du membre (TEC-1677).
  const { org: canSeeOrg, billing: canSeeBilling } = useSettingsAccess(user?.role)
  const hyperlineEnabled = useHyperlineFeature()

  const { data: billingSubscription } = useBillingSubscription(
    canSeeBilling && hyperlineEnabled
  )
  const isBillingEnrolled = billingSubscription?.enrolled === true

  const [agencyCount, setAgencyCount] = useState<number | null>(null)

  useEffect(() => {
    if (!canSeeOrg) {
      setAgencyCount(null)
      return
    }
    void callAccountGroups()
      .then((res) => setAgencyCount(countAgencyGroups(res.data ?? [])))
      .catch(() => setAgencyCount(null))
  }, [canSeeOrg])

  // const goBack = () => {
  //   if (typeof window !== 'undefined' && window.history.length > 1) {
  //     navigate(-1)
  //   } else {
  //     navigate(paths.dashboard.settings.account.profile)
  //   }
  // }

  const inactiveNavColor =
    theme.palette.neutral?.[500] ?? theme.palette.text.secondary
  const subtleText =
    theme.palette.neutral?.[500] ?? theme.palette.text.secondary

  return (
    <Box
      component="nav"
      sx={{
        width: 248,
        flexShrink: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        height: `calc(100vh - ${APP_FRAME_GAP})`,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        p: '20px 18px 32px 24px',
      }}
    >
      {/* <ButtonBase
        onClick={goBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          justifyContent: 'flex-start',
          px: '2px',
          py: '6px',
          mb: '18px',
          borderRadius: 1,
          color: inactiveNavColor,
          fontSize: 12,
          fontWeight: 500,
          '&:hover': { color: theme.palette.text.primary },
        }}
      >
        <SvgIcon sx={{ fontSize: NAV_ICON_PX }}>
          <ArrowLeft style={{ display: 'block' }} />
        </SvgIcon>

        <span>{t('back')}</span>
      </ButtonBase> */}

      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: 'subtleText',
          // textTransform: 'uppercase',
          letterSpacing: '0.8px',
          mb: '16px',
          pl: '4px',
        }}
      >
        {t('sidebarKicker')}
      </Typography>

      <Stack spacing={0} sx={{ flex: 1 }}>
        {NAV_GROUPS.map((group) => {
          if (group.adminOnly && !canSeeOrg) return null
          const visibleItems = group.items.filter((item) => {
            if (
              item.featureFlag &&
              !isFeatureEnabled(item.featureFlag)
            ) {
              return false
            }
            if (item.billingAdminOnly && !canSeeBilling) return false
            if (item.requiresEnrollment && !isBillingEnrolled) return false
            if (item.requiresAuthzScreens && !authzScreens.enabled) return false
            return true
          })
          if (visibleItems.length === 0) return null
          return (
            <Box key={group.labelKey} sx={{ mb: '22px' }}>
              <Box
                sx={{
                  px: '4px',
                  pb: '8px',
                  fontSize: 9,
                  fontWeight: 700,
                  color: subtleText,
                  textTransform: 'uppercase',
                  letterSpacing: '1.1px',
                }}
              >
                {t(group.labelKey)}
              </Box>
              {visibleItems.map((item) => {
                const active =
                  pathname === item.path ||
                  pathname.startsWith(item.path + '/')
                const Icon = item.icon
                const accent = item.accentColor ?? theme.palette.primary.main
                return (
                  <ButtonBase
                    key={item.id}
                    onClick={() => confirmLeave(() => navigate(item.path))}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      px: '10px',
                      py: '7px',
                      borderRadius: '6px',
                      fontSize: 11,
                      mb: '1px',
                      bgcolor: active ? alpha(accent, 0.1) : 'transparent',
                      color: active ? accent : theme.palette.text.primary,
                      fontWeight: active ? 600 : 400,
                      borderLeft: active
                        ? `2px solid ${accent}`
                        : '2px solid transparent',
                      transition: 'all .12s ease',
                      '&:hover': {
                        bgcolor: active
                          ? alpha(accent, 0.1)
                          : alpha(
                              theme.palette.neutral?.[900] ?? '#111927',
                              0.04
                            ),
                      },
                    }}
                  >
                      <SvgIcon sx={{ fontSize: NAV_ICON_PX }}>
                        <Icon
                          style={{
                            flexShrink: 0,
                            color: active
                              ? accent
                              : item.accentColor ?? inactiveNavColor,
                            display: 'block',
                          }}
                        />
                      </SvgIcon>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 'inherit',
                          color: 'inherit',
                          flex: 1,
                        }}
                      >
                        {t(item.labelKey)}
                      </Typography>
                      {item.id === 'members' && <MembersNavCountChip />}
                      {item.id === 'agencies' && agencyCount !== null && (
                        <Chip
                          label={agencyCount}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 22,
                            flexShrink: 0,
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? alpha(
                                    theme.palette.neutral?.[700] ?? '#374151',
                                    0.6
                                  )
                                : theme.palette.grey[200],
                            color:
                              theme.palette.neutral?.[600] ??
                              theme.palette.text.secondary,
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 0.75,
                              fontSize: 10,
                              lineHeight: 1.2,
                            },
                          }}
                        />
                      )}
                    </ButtonBase>
                  )
                })}
            </Box>
          )
        })}
      </Stack>

      <Box
        sx={{
          p: '14px 10px',
          mt: 1,
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.neutral?.[900] ?? '#111', 0.5)
              : '#F7F9FB',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {t('helpBoxTitle')}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: subtleText,
            mt: '4px',
            mb: '10px',
            lineHeight: 1.45,
          }}
        >
          {t('helpBoxBody')}
        </Typography>
        <ButtonBase
          onClick={() =>
            confirmLeave(() => navigate(paths.dashboard.settings.support.help))
          }
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.palette.primary.main,
            fontSize: 11,
            fontWeight: 500,
            p: 0,
          }}
        >
          <Stack gap={0.5} direction="row" alignItems="center">
            {t('helpDocumentationLink')}
            <SvgIcon sx={{ fontSize: DOC_ICON_PX, display: 'block' }}>
              <Share03 style={{ display: 'block' }} />
            </SvgIcon>
          </Stack>
        </ButtonBase>
      </Box>
    </Box>
  )
}
